// src/hooks/app-config/useAppConfig.tsx

import {
  useEffect,
  useState,
  useContext,
  createContext,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react'

import { SalutationMode } from '@/types/karma/operator'

import { getStorage, setStorage } from '../../api/'

export type AppConfig = {
  showMissingSensorDataNotice?: boolean
  setupTab?: number
  salutationMode?: SalutationMode
  alertsEnabled?: boolean
  alertsMax?: number
  timeMode?: 'relative' | 'absolute'
  isMaintenance?: boolean
  maintenanceMessage?: string
  alertsAcknowledged?: number[]
  showSettings?: boolean
  wideScreen?: boolean
}

export type AppConfigContextValue = {
  daytimeGreeting: () => string
  appConfig: AppConfig
  setAppConfig: Dispatch<SetStateAction<AppConfig>>
}

type AppConfigProviderProps = {
  children: ReactNode
}

const AppConfigContext = createContext<AppConfigContextValue | undefined>(
  undefined
)

export const AppConfigProvider = ({ children }: AppConfigProviderProps) => (
  <AppConfigContext.Provider value={useConfig()}>
    {children}
  </AppConfigContext.Provider>
)

export const useAppConfig = (): AppConfigContextValue => {
  const context = useContext(AppConfigContext)

  if (!context)
    throw new Error('useAppConfig must be used within AppConfigProvider')

  return context
}

const daytimeGreeting = (): string => {
  const h = new Date().getHours()

  return h >= 0 && h < 10
    ? 'Guten Morgen'
    : h >= 10 && h < 18
      ? 'Guten Tag'
      : 'Guten Abend'
}

const useConfig = (): AppConfigContextValue => {
  const PAGE_CONFIG = 'settings_global'
  const [appConfig, setAppConfig] = useState<AppConfig>({})

  useEffect(() => {
    const storedConfig = getStorage(PAGE_CONFIG) as AppConfig
    setAppConfig(storedConfig ?? {})
  }, [])

  useEffect(() => {
    setStorage(PAGE_CONFIG, appConfig)
  }, [appConfig])

  return { daytimeGreeting, appConfig, setAppConfig }
}
